import {
  computeRowLayout,
  moreBeyondView,
  type RowLayoutInput,
  type RowView,
} from './wholeCardRow';

/** What the row measured of itself, and so how it is laid out. */
export type MeasuredRow = {
  /** The cards do not all fit, so only whole cards show and the new-one card is pinned. */
  overflowing: boolean;
  view: RowView;
};

/** Whether there is more to the left, and more to the right, of what is in view. */
export type More = { left: boolean; right: boolean };

export const NOTHING_TO_SCROLL: MeasuredRow = {
  overflowing: false,
  view: { count: 0, visibleCount: 0, cardWidth: 0, step: 0, viewWidth: 0 },
};
export const NO_MORE: More = { left: false, right: false };

// Layout reads give fractions of a pixel; a change smaller than this is the same layout.
const SAME_WITHIN_PX = 0.01;

function isSameNumber(a: number, b: number): boolean {
  return Math.abs(a - b) < SAME_WITHIN_PX;
}

export function isSameRow(a: MeasuredRow, b: MeasuredRow): boolean {
  return (
    a.overflowing === b.overflowing &&
    a.view.count === b.view.count &&
    a.view.visibleCount === b.view.visibleCount &&
    isSameNumber(a.view.cardWidth, b.view.cardWidth) &&
    isSameNumber(a.view.step, b.view.step) &&
    isSameNumber(a.view.viewWidth, b.view.viewWidth)
  );
}

export function isSameMore(a: More, b: More): boolean {
  return a.left === b.left && a.right === b.right;
}

export type Plan = {
  row: MeasuredRow;
  /** The width every card takes, or null when there are no cards. */
  cardWidth: number | null;
};

/** The layout for what was measured: how wide each card is and which cards show. */
export function planRow(measured: Readonly<RowLayoutInput> | null): Plan {
  if (!measured) {
    return { row: NOTHING_TO_SCROLL, cardWidth: null };
  }
  const { overflowing, pageSize, cardWidth } = computeRowLayout(measured);
  const step = cardWidth + measured.gap;
  return {
    row: {
      overflowing,
      view: {
        count: measured.count,
        visibleCount: pageSize,
        cardWidth,
        step,
        viewWidth: pageSize * step - measured.gap,
      },
    },
    cardWidth,
  };
}

/** What lies beyond the edges of the view at the scroller's current position. */
export function readMore(row: MeasuredRow, scroller: HTMLElement): More {
  if (!row.overflowing) {
    return NO_MORE;
  }
  return moreBeyondView({ ...row.view, scrollLeft: scroller.scrollLeft });
}

/** A scroll the row asked for itself that may not have arrived yet. */
export type PendingScroll = { left: number; until: number; smooth: boolean };

// A smooth scroll takes well under this. After it, the scroll position is the truth again, even if
// the customer has taken the scroll over and it never arrived. An instant one only needs its
// scroll event, which comes a frame later.
const SMOOTH_SCROLL_MS = 1000;
const INSTANT_SCROLL_MS = 100;

export function startPending(left: number, smooth: boolean): PendingScroll {
  return {
    left,
    smooth,
    until: performance.now() + (smooth ? SMOOTH_SCROLL_MS : INSTANT_SCROLL_MS),
  };
}

/** Whether a scroll the row asked for may still be on its way, so a scroll event is its own. */
export function isInFlight(pending: PendingScroll | null): pending is PendingScroll {
  return pending !== null && performance.now() < pending.until;
}

/**
 * Where the row is going to rest: the end of a scroll in flight, else where it is. Paging and
 * following focus work from this, so that a key press while an arrow's scroll is on its way is
 * judged against where the row will be, not against where it passes.
 */
export function restingLeft(scroller: HTMLElement, pending: PendingScroll | null): number {
  return isInFlight(pending) ? pending.left : scroller.scrollLeft;
}

/** Whether the scroll position has reached what the row asked it to reach. */
export function hasArrived(scroller: HTMLElement, pending: PendingScroll | null): boolean {
  return pending !== null && Math.abs(scroller.scrollLeft - pending.left) < 1;
}

/** Whether a smooth scroll the row asked for is still on its way. */
export function isSmoothInFlight(pending: PendingScroll | null): boolean {
  return isInFlight(pending) && pending.smooth;
}
